/* ================================================================== *
 * Agnieszka Pyszczak — gallery theme behaviour
 * Ported from the Next.js build: Lenis smooth scroll on the GSAP ticker,
 * the dark-room hero intro, the snowfall, the custom cursor, and the
 * scroll-reveal observer. Plain JS, no framework.
 * ================================================================== */
(function () {
  document.documentElement.classList.add("js");
  var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function initSmoothScroll(gsap, ScrollTrigger) {
    if (reduced || !window.Lenis) return;
    var lenis = new window.Lenis({
      duration: 1.15,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
    });
    window.__lenis = lenis;
    if (ScrollTrigger) lenis.on("scroll", ScrollTrigger.update);
    gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
    gsap.ticker.lagSmoothing(0);
  }

  function initHero(gsap, ScrollTrigger) {
    var root = document.querySelector("[data-ap-hero]");
    if (!root) return;
    var plate = root.querySelector(".ap-hero__plate");
    var title = root.querySelector(".ap-hero__title");
    var chars = root.querySelectorAll(".ap-char");
    var eyebrow = root.querySelector(".ap-hero__eyebrow");
    var sub = root.querySelector(".ap-hero__sub");
    var cue = root.querySelector(".ap-hero__cue");
    if (!plate || !title || !eyebrow || !sub || !cue) return;
    var all = [plate, title, eyebrow, sub, cue].concat(Array.prototype.slice.call(chars));
    if (reduced) { gsap.set(all, { clearProps: "all", opacity: 1 }); return; }

    gsap.set(plate, { opacity: 0, scale: 1.12, yPercent: 4 });
    gsap.set(chars, { yPercent: 115 });
    gsap.set([sub, cue, eyebrow], { opacity: 0, y: 16 });

    function buildScrubs() {
      if (!ScrollTrigger) return;
      gsap.fromTo(plate, { yPercent: 0, scale: 1 },
        { yPercent: 14, scale: 1.05, ease: "none", immediateRender: false,
          scrollTrigger: { trigger: root, start: "top top", end: "bottom top", scrub: true } });
      gsap.fromTo(cue, { opacity: 1 },
        { opacity: 0, ease: "none", immediateRender: false,
          scrollTrigger: { trigger: root, start: "top top", end: "25% top", scrub: true } });
    }

    var tl = gsap.timeline({ defaults: { ease: "power3.out" }, onComplete: buildScrubs });
    tl.to(plate, { opacity: 1, scale: 1, yPercent: 0, duration: 1.6, ease: "power2.out" })
      .to(chars, { yPercent: 0, duration: 1.05, stagger: 0.028, ease: "power4.out" }, 0.35)
      .to(eyebrow, { opacity: 1, y: 0, duration: 0.8 }, 0.9)
      .to(sub, { opacity: 1, y: 0, duration: 0.8 }, 1.05)
      .to(cue, { opacity: 1, y: 0, duration: 0.8 }, 1.2);

    if (ScrollTrigger) {
      gsap.to(title, { yPercent: -22, opacity: 0.25, ease: "none",
        scrollTrigger: { trigger: root, start: "top top", end: "70% top", scrub: true } });
    }
  }

  function initSnow() {
    if (reduced) return;
    var canvas = document.querySelector(".ap-hero__snow");
    if (!canvas) return;
    var ctx = canvas.getContext("2d");
    if (!ctx) return;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var width = 0, height = 0, flakes = [], raf = 0, running = false;
    function spawn() {
      var count = width < 768 ? 55 : 120;
      flakes = [];
      for (var i = 0; i < count; i++) {
        flakes.push({
          x: Math.random() * width, y: Math.random() * height,
          r: 0.5 + Math.random() * 1.7, vy: 0.18 + Math.random() * 0.5,
          vx: (Math.random() - 0.5) * 0.25, o: 0.15 + Math.random() * 0.55,
          phase: Math.random() * Math.PI * 2,
        });
      }
    }
    function resize() {
      if (canvas.clientWidth === width && canvas.clientHeight === height) return;
      width = canvas.clientWidth; height = canvas.clientHeight;
      canvas.width = Math.round(width * dpr); canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0); spawn();
    }
    function draw(t) {
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = "#f5f2ec";
      for (var i = 0; i < flakes.length; i++) {
        var f = flakes[i];
        f.y += f.vy; f.x += f.vx + Math.sin(t / 2400 + f.phase) * 0.18;
        if (f.y > height + 4) { f.y = -4; f.x = Math.random() * width; }
        if (f.x > width + 4) f.x = -4;
        if (f.x < -4) f.x = width + 4;
        ctx.globalAlpha = f.o; ctx.beginPath(); ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2); ctx.fill();
      }
      ctx.globalAlpha = 1; raf = requestAnimationFrame(draw);
    }
    function start() { if (running) return; running = true; raf = requestAnimationFrame(draw); }
    function stop() { running = false; cancelAnimationFrame(raf); }
    resize();
    if ("IntersectionObserver" in window) {
      new IntersectionObserver(function (e) { e[0].isIntersecting ? start() : stop(); }, { threshold: 0 }).observe(canvas);
    } else { start(); }
    window.addEventListener("resize", resize);
  }

  function initReveal() {
    var els = document.querySelectorAll(".ap-reveal");
    if (!els.length) return;
    if (reduced || !("IntersectionObserver" in window)) {
      els.forEach(function (el) { el.classList.add("is-visible"); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { entry.target.classList.add("is-visible"); io.unobserve(entry.target); }
      });
    }, { threshold: 0.12, rootMargin: "0px 0px -8% 0px" });
    els.forEach(function (el) { io.observe(el); });
  }

  function initManifesto(gsap, ScrollTrigger) {
    if (reduced || !ScrollTrigger || !window.SplitText) return;
    var el = document.querySelector(".ap-manifesto__text");
    if (!el) return;
    (document.fonts ? document.fonts.ready : Promise.resolve()).then(function () {
      if (!el.isConnected) return;
      var split = new window.SplitText(el, { type: "words" });
      gsap.from(split.words, {
        opacity: 0.12, ease: "none", stagger: 0.05,
        scrollTrigger: { trigger: el, start: "top 78%", end: "bottom 45%", scrub: true },
      });
    });
  }

  function initFeatured(gsap, ScrollTrigger) {
    if (reduced || !ScrollTrigger) return;
    document.querySelectorAll(".ap-fw-row").forEach(function (row) {
      var img = row.querySelector(".ap-fw-img");
      var meta = row.querySelector(".ap-fw-meta");
      if (img) {
        gsap.fromTo(img, { yPercent: -8, scale: 1.12 },
          { yPercent: 8, scale: 1.12, ease: "none",
            scrollTrigger: { trigger: row, start: "top bottom", end: "bottom top", scrub: true } });
      }
      if (meta) {
        gsap.from(meta.children, { y: 36, opacity: 0, duration: 1, stagger: 0.08, ease: "power3.out",
          scrollTrigger: { trigger: row, start: "top 68%" } });
      }
    });
  }

  function initCollections(gsap, ScrollTrigger) {
    if (!ScrollTrigger || !gsap.matchMedia) return;
    var mm = gsap.matchMedia();
    mm.add("(min-width: 768px) and (prefers-reduced-motion: no-preference)", function () {
      var root = document.querySelector("[data-ap-collections]");
      var track = document.querySelector("[data-ap-collections-track]");
      if (!root || !track) return;
      var distance = function () { return track.scrollWidth - window.innerWidth; };
      gsap.to(track, { x: function () { return -distance(); }, ease: "none",
        scrollTrigger: { trigger: root, start: "top top", end: function () { return "+=" + distance(); },
          pin: true, scrub: 1, invalidateOnRefresh: true } });
    });
  }

  function initAcquire(gsap, ScrollTrigger) {
    if (reduced || !ScrollTrigger) return;
    var head = document.querySelector(".ap-acquire__head");
    if (head) gsap.from(head.children, { y: 34, opacity: 0, duration: 1, stagger: 0.1, ease: "power3.out",
      scrollTrigger: { trigger: head, start: "top 75%" } });
    var items = document.querySelectorAll(".ap-acq-item");
    if (items.length) gsap.from(items, { y: 40, opacity: 0, duration: 0.9, stagger: 0.09, ease: "power3.out",
      scrollTrigger: { trigger: ".ap-acquire__grid", start: "top 78%" } });
  }

  function initMagnet(gsap) {
    if (!window.matchMedia("(pointer: fine)").matches || reduced) return;
    var magnet = document.querySelector("[data-ap-magnet]");
    var zone = document.querySelector("[data-ap-magnet-zone]");
    if (!magnet || !zone) return;
    var xTo = gsap.quickTo(magnet, "x", { duration: 0.5, ease: "power3.out" });
    var yTo = gsap.quickTo(magnet, "y", { duration: 0.5, ease: "power3.out" });
    var leaveTween = null;
    zone.addEventListener("mousemove", function (e) {
      if (leaveTween) { leaveTween.kill(); leaveTween = null; }
      var rect = magnet.getBoundingClientRect();
      xTo((e.clientX - (rect.left + rect.width / 2)) * 0.22);
      yTo((e.clientY - (rect.top + rect.height / 2)) * 0.32);
    });
    zone.addEventListener("mouseleave", function () {
      leaveTween = gsap.to(magnet, { x: 0, y: 0, duration: 0.9, ease: "elastic.out(1, 0.4)", overwrite: "auto" });
    });
  }

  function money(cents) { return "$" + (cents / 100).toFixed(2); }

  function initCart() {
    var drawer = document.querySelector("[data-cart-drawer]");
    var body = drawer ? drawer.querySelector("[data-cart-body]") : null;
    var foot = drawer ? drawer.querySelector("[data-cart-foot]") : null;
    var subtotalEl = drawer ? drawer.querySelector("[data-drawer-subtotal]") : null;
    var counts = document.querySelectorAll("[data-cart-count]");

    function setCount(n) { counts.forEach(function (c) { c.textContent = n; }); }

    function render(cart) {
      setCount(cart.item_count);
      if (!body) return;
      if (!cart.items || cart.items.length === 0) {
        body.innerHTML = '<p class="ap-drawer__empty">Your cart is empty.</p>';
        if (foot) foot.hidden = true;
        return;
      }
      var html = "";
      cart.items.forEach(function (it) {
        var name = (it.product_title || it.title || "").split(" — ")[0];
        var img = it.image ? '<img src="' + it.image + '&width=120" alt="" loading="lazy">' : "";
        html +=
          '<div class="ap-dl-item" data-key="' + it.key + '">' +
          '<a class="ap-dl-item__media" href="' + it.url + '">' + img + "</a>" +
          "<div><p class=\"ap-dl-item__name\">" + name + "</p>" +
          '<p class="ap-dl-item__meta">' + (it.variant_title || "") + "</p>" +
          '<p class="ap-dl-item__qty">Qty ' + it.quantity + "</p>" +
          '<button type="button" class="ap-dl-item__remove" data-dl-remove="' + it.key + '">Remove</button></div>' +
          '<p class="ap-dl-item__price">' + money(it.final_line_price) + "</p>" +
          "</div>";
      });
      body.innerHTML = html;
      if (subtotalEl) subtotalEl.textContent = money(cart.total_price);
      if (foot) foot.hidden = false;
      body.querySelectorAll("[data-dl-remove]").forEach(function (btn) {
        btn.addEventListener("click", function () { change(btn.getAttribute("data-dl-remove"), 0); });
      });
    }

    function fetchCart(cb) {
      fetch("/cart.js", { headers: { Accept: "application/json" } })
        .then(function (r) { return r.json(); })
        .then(function (c) { render(c); if (cb) cb(c); })
        .catch(function () {});
    }

    function open() {
      if (!drawer) return;
      drawer.classList.add("is-open");
      drawer.setAttribute("aria-hidden", "false");
      document.body.classList.add("ap-noscroll");
    }
    function close() {
      if (!drawer) return;
      drawer.classList.remove("is-open");
      drawer.setAttribute("aria-hidden", "true");
      document.body.classList.remove("ap-noscroll");
    }

    function change(key, qty) {
      fetch("/cart/change.js", {
        method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: key, quantity: qty }),
      }).then(function (r) { return r.json(); }).then(render).catch(function () {});
    }

    function add(id, qty, btn, label) {
      var prev = label ? label.textContent : "";
      if (label) label.textContent = "Adding…";
      if (btn) btn.disabled = true;
      fetch("/cart/add.js", {
        method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: id, quantity: qty }),
      })
        .then(function (r) { if (!r.ok) throw new Error("add failed"); return r.json(); })
        .then(function () {
          fetchCart(function () { open(); });
          if (label) label.textContent = "Added ✓";
          setTimeout(function () { if (label) label.textContent = prev || "Add to cart"; if (btn) btn.disabled = false; }, 1400);
        })
        .catch(function () { if (label) label.textContent = prev || "Add to cart"; if (btn) btn.disabled = false; window.location.href = "/cart"; });
    }

    window.__apCart = { add: add, open: open, close: close, refresh: fetchCart };

    document.querySelectorAll("[data-cart-toggle]").forEach(function (b) {
      b.addEventListener("click", function () { fetchCart(function () { open(); }); });
    });
    if (drawer) drawer.querySelectorAll("[data-cart-close]").forEach(function (b) { b.addEventListener("click", close); });
    document.addEventListener("keydown", function (e) { if (e.key === "Escape") close(); });

    fetchCart();
  }

  function initProduct() {
    var root = document.querySelector("[data-ap-product]");
    if (!root) return;
    var form = root.querySelector("form.ap-pi-form") || root.querySelector('form[action*="/cart/add"]') || root.querySelector("form");
    var idInput = root.querySelector("[data-variant-id]");
    var priceEl = root.querySelector("[data-price]");
    var scarcityEl = root.querySelector("[data-scarcity]");
    var addBtn = root.querySelector("[data-add-btn]");
    var addLabel = root.querySelector("[data-add-label]");
    var opts = root.querySelectorAll("[data-variant-opt]");

    function setScarcity(inv, available) {
      if (!scarcityEl) return;
      var n = parseInt(inv, 10);
      if (!available) { scarcityEl.textContent = "Sold out"; return; }
      if (!isNaN(n) && n > 0 && n <= 50) scarcityEl.textContent = n + " of 50 remaining in this size";
      else scarcityEl.textContent = "Edition of 50 · signed and numbered";
    }

    function selectVariant(input) {
      if (idInput) idInput.value = input.value;
      if (priceEl) priceEl.textContent = input.getAttribute("data-price");
      var available = input.getAttribute("data-available") === "true";
      setScarcity(input.getAttribute("data-inv"), available);
      if (addBtn) addBtn.disabled = !available;
      if (addLabel) addLabel.textContent = available ? "Add to cart" : "Sold out";
      root.querySelectorAll(".ap-sz").forEach(function (l) { l.classList.remove("is-selected"); });
      var label = input.closest(".ap-sz"); if (label) label.classList.add("is-selected");
    }

    opts.forEach(function (input) {
      input.addEventListener("change", function () { selectVariant(input); });
      if (input.checked) selectVariant(input);
    });
    if (!opts.length && scarcityEl) scarcityEl.textContent = "Edition of 50 · signed and numbered";

    if (form) {
      form.addEventListener("submit", function (e) {
        if (!window.__apCart) return;
        e.preventDefault();
        var id = idInput ? idInput.value : null;
        if (id) window.__apCart.add(id, 1, addBtn, addLabel);
      });
    }
  }

  function initCartPage() {
    var page = document.querySelector("[data-ap-cart-page]");
    if (!page) return;
    page.querySelectorAll(".ap-qty").forEach(function (wrap) {
      var input = wrap.querySelector(".ap-qty__input");
      var down = wrap.querySelector("[data-qty-down]");
      var up = wrap.querySelector("[data-qty-up]");
      if (down) down.addEventListener("click", function () { input.value = Math.max(0, (parseInt(input.value, 10) || 0) - 1); });
      if (up) up.addEventListener("click", function () { input.value = (parseInt(input.value, 10) || 0) + 1; });
    });
  }

  function init() {
    initCart();
    initProduct();
    initCartPage();
    var gsap = window.gsap;
    if (!gsap) { initReveal(); return; }
    var ScrollTrigger = window.ScrollTrigger;
    if (ScrollTrigger) gsap.registerPlugin(ScrollTrigger);
    if (window.SplitText) gsap.registerPlugin(window.SplitText);
    initSmoothScroll(gsap, ScrollTrigger);
    initHero(gsap, ScrollTrigger);
    initSnow();
    initManifesto(gsap, ScrollTrigger);
    initFeatured(gsap, ScrollTrigger);
    initCollections(gsap, ScrollTrigger);
    initAcquire(gsap, ScrollTrigger);
    initMagnet(gsap);
    initReveal();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
